import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaEdit,
  FaFilePdf,
  FaEnvelope,
  FaCheck,
  FaDownload,
  FaExternalLinkAlt,
  FaSync,
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSpinner,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import InvoiceService, { Invoice, InvoiceStatus } from '../services/InvoiceService';
import BusinessPartnerService, { BusinessPartner } from '../../contacts/services/BusinessPartnerService';
import TenantService, { BusinessProfile } from '../../settings/company/services/TenantService';
import EmailTemplateService from '../../settings/email-templates/services/EmailTemplateService';

interface PdfDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfBlobUrl: string | null;
  onRefresh: () => void;
  loading: boolean;
  invoiceNumber: string;
}

function PdfDrawer({ isOpen, onClose, pdfBlobUrl, onRefresh, loading, invoiceNumber }: PdfDrawerProps): React.JSX.Element | null {
  if (!isOpen) return null;

  const handleDownload = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = `Invoice_${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '700px',
          maxWidth: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                color: '#666',
                display: 'flex',
                alignItems: 'center',
              }}
              title="Close"
            >
              <FaTimes size={16} />
            </button>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
              PDF Preview
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onRefresh}
              disabled={loading}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                border: '1px solid #1976d2',
                borderRadius: '4px',
                cursor: loading ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FaSync size={12} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {pdfBlobUrl && (
              <>
                <a
                  href={pdfBlobUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: '#fff',
                    color: '#1976d2',
                    border: '1px solid #1976d2',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FaExternalLinkAlt size={12} />
                  New tab
                </a>
                <button
                  onClick={handleDownload}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: '#e8f5e9',
                    color: '#388e3c',
                    border: '1px solid #388e3c',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FaDownload size={12} />
                  Download
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'hidden', background: '#f5f5f5' }}>
          {pdfBlobUrl ? (
            <iframe
              src={pdfBlobUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="Invoice PDF"
            />
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#888',
              }}
            >
              <FaFilePdf size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p style={{ margin: 0 }}>
                {loading ? 'Loading PDF preview...' : 'Click "Refresh" to load the PDF'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface SendEmailModalProps {
  isOpen: boolean;
  invoice: Invoice;
  partnerEmail: string;
  onClose: () => void;
  onSent: () => void;
}

function SendEmailModal({ isOpen, invoice, partnerEmail, onClose, onSent }: SendEmailModalProps): React.JSX.Element | null {
  const [email, setEmail] = useState(partnerEmail);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sendAsMe, setSendAsMe] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [companyProfile, setCompanyProfile] = useState<BusinessProfile | null>(null);

  const stripHtmlTags = (html: string): string => {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const getDefaultMessage = (inv: Invoice): string => {
    return `Sehr geehrte Damen und Herren,

anbei erhalten Sie unsere Rechnung ${inv.invoice_number}.

Rechnungsbetrag: ${InvoiceService.formatCurrency(inv.gross_total)}
Zahlungsziel: ${inv.due_date ? new Date(inv.due_date).toLocaleDateString('de-DE') : ''}

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen`;
  };

  useEffect(() => {
    if (isOpen) {
      setEmail(partnerEmail);
      setSendAsMe(false);

      TenantService.getBusinessProfile()
        .then(setCompanyProfile)
        .catch((err) => console.error('Failed to load company profile:', err));

      setLoadingTemplate(true);
      EmailTemplateService.preview('INVOICE', { invoice_id: invoice.id })
        .then((preview) => {
          setSubject(preview.subject);
          setMessage(preview.body_text || stripHtmlTags(preview.body_html));
        })
        .catch((err) => {
          console.error('Failed to load template preview:', err);
          setSubject(`Rechnung ${invoice.invoice_number}`);
          setMessage(getDefaultMessage(invoice));
        })
        .finally(() => setLoadingTemplate(false));
    }
  }, [isOpen, invoice, partnerEmail]);

  const handleSend = async () => {
    if (!email) {
      toast.error('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      await InvoiceService.sendEmail(invoice.id, {
        recipient_email: email,
        subject,
        message,
        send_as_me: sendAsMe,
      });
      toast.success('Rechnung per E-Mail gesendet');
      onSent();
    } catch (err: any) {
      console.error('Failed to send email:', err);
      const detail = err?.response?.data?.detail || '';
      let errorMessage = 'E-Mail konnte nicht gesendet werden';

      if (detail.toLowerCase().includes('sendgrid') || err?.response?.status === 401) {
        errorMessage = 'E-Mail-Versand fehlgeschlagen (SendGrid nicht konfiguriert). Bitte kontaktieren Sie den Support.';
      } else if (detail) {
        errorMessage = detail;
      }

      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            Rechnung per E-Mail senden
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div style={{ padding: '24px' }}>
          {/* Sender Info */}
          {companyProfile && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '10px 14px',
                background: '#f0fdf4',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#166534',
              }}
            >
              <FaInfoCircle style={{ marginTop: '2px', flexShrink: 0 }} />
              <div>
                <strong>Von:</strong> {companyProfile.default_sender_email || 'noreply@sundaeos.com'}
                {companyProfile.reply_to_email && (
                  <span style={{ marginLeft: '12px' }}>
                    <strong>Antworten an:</strong> {companyProfile.reply_to_email}
                  </span>
                )}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Empfänger E-Mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              placeholder="kunde@beispiel.de"
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>
              Betreff
              {loadingTemplate && <FaSpinner className="spin" style={{ marginLeft: '8px', color: '#7c3aed' }} size={12} />}
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={loadingTemplate ? 'Lade Template...' : 'Betreff der E-Mail'}
              style={inputStyle}
              disabled={loadingTemplate}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Nachricht
              {loadingTemplate && <FaSpinner className="spin" style={{ marginLeft: '8px', color: '#7c3aed' }} size={12} />}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={loadingTemplate ? 'Lade Template...' : 'Ihre Nachricht...'}
              style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
              disabled={loadingTemplate}
            />
          </div>

          {/* Template Note */}
          <div
            style={{
              padding: '12px 16px',
              background: '#dbeafe',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#1e40af',
            }}
          >
            <div>Die Rechnung wird als PDF-Anhang an die E-Mail angefügt.</div>
            <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.85 }}>
              Diese E-Mail basiert auf Ihrem Standard-Template. Änderungen gelten nur für diese E-Mail.
            </div>
          </div>

          {/* Send as me checkbox */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
              }}
            >
              <input
                type="checkbox"
                checked={sendAsMe}
                onChange={(e) => setSendAsMe(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#7c3aed',
                }}
              />
              <span>
                Als ich senden
                <span style={{ color: '#6b7280', fontSize: '13px', marginLeft: '6px' }}>
                  (Antworten gehen an meine E-Mail)
                </span>
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              disabled={sending}
              style={{
                padding: '10px 24px',
                border: '1px solid #e0e3ef',
                borderRadius: '6px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              style={{
                padding: '10px 24px',
                border: 'none',
                borderRadius: '6px',
                background: sending ? '#ccc' : '#021a4a',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
              }}
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceDetailPage(): React.JSX.Element {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [partner, setPartner] = useState<BusinessPartner | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [showPdfDrawer, setShowPdfDrawer] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadInvoice = useCallback(async () => {
    if (!invoiceId) return;

    setLoading(true);
    try {
      const invoiceData = await InvoiceService.get(parseInt(invoiceId, 10));
      setInvoice(invoiceData);

      if (invoiceData.partner) {
        try {
          const partnerData = await BusinessPartnerService.get(invoiceData.partner);
          setPartner(partnerData);
        } catch {
          setPartner(null);
        }
      }
    } catch (err) {
      console.error('Failed to load invoice:', err);
      toast.error('Failed to load invoice');
      navigate('/sales');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, navigate]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  const handleBack = () => {
    navigate('/sales?tab=invoices');
  };

  const handleEdit = () => {
    if (invoice) {
      navigate(`/invoices/${invoice.id}/edit`);
    }
  };

  const handleLoadPdf = async () => {
    if (!invoice) return;

    setShowPdfDrawer(true);
    setLoadingPdf(true);
    try {
      await InvoiceService.renderPdf(invoice.id);
      const blob = await InvoiceService.downloadPdf(invoice.id);
      const url = URL.createObjectURL(blob);

      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }

      setPdfBlobUrl(url);
    } catch (err) {
      console.error('Failed to load PDF:', err);
      toast.error('Failed to load PDF');
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;

    try {
      const blob = await InvoiceService.downloadPdf(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnung_${invoice.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      toast.error('Failed to download PDF');
    }
  };

  const handleStatusChange = async (status: InvoiceStatus) => {
    if (!invoice) return;

    setActionLoading(true);
    try {
      await InvoiceService.changeStatus(invoice.id, status);
      toast.success(`Invoice marked as ${InvoiceService.getStatusLabel(status)}`);
      loadInvoice();
    } catch (err) {
      console.error('Failed to change status:', err);
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEmailSent = () => {
    setShowEmailModal(false);
    loadInvoice();
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isOverdue = (): boolean => {
    if (!invoice || invoice.status === 'PAID' || invoice.status === 'CANCELLED') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(invoice.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  if (loading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
        Loading...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#666' }}>
        Invoice not found
      </div>
    );
  }

  const statusColor = InvoiceService.getStatusColor(invoice.status);
  const partnerName = partner ? BusinessPartnerService.getDisplayName(partner) : invoice.partner_name || '-';
  const canEdit = invoice.status === 'DRAFT';
  const overdue = isOverdue();

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <button
        onClick={handleBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          marginBottom: '16px',
          border: 'none',
          background: 'none',
          color: '#666',
          fontSize: '14px',
          cursor: 'pointer',
        }}
      >
        <FaArrowLeft size={14} />
        Back to Invoices
      </button>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px 32px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#021a4a' }}>
                {invoice.title}
              </h1>
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: statusColor.bg,
                  color: statusColor.text,
                }}
              >
                {InvoiceService.getStatusLabel(invoice.status)}
              </span>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: invoice.kind === 'DEPOSIT' ? '#fef3c7' : '#e0e7ff',
                  color: invoice.kind === 'DEPOSIT' ? '#92400e' : '#3730a3',
                }}
              >
                {InvoiceService.getKindLabel(invoice.kind)}
              </span>
              {overdue && (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: '#fee2e2',
                    color: '#dc2626',
                  }}
                >
                  <FaExclamationTriangle size={12} />
                  Overdue
                </span>
              )}
            </div>
            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
              No.: {invoice.invoice_number}
            </p>
            <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
              Customer: {partnerName}
            </p>
            {invoice.offer_number && (
              <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#666' }}>
                From offer: {invoice.offer_number}
              </p>
            )}
            {invoice.project_name && (
              <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                Project: {invoice.project_name}
              </p>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '4px', color: '#021a4a' }}>
              {InvoiceService.formatCurrency(invoice.gross_total)}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Issue date: {formatDate(invoice.issue_date)}
            </div>
            <div style={{ fontSize: '14px', color: overdue ? '#dc2626' : '#666', fontWeight: overdue ? 600 : 400 }}>
              Due date: {formatDate(invoice.due_date)}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px', marginTop: '20px', flexWrap: 'wrap' }}>
          {canEdit && (
            <button
              onClick={handleEdit}
              style={actionBtnStyle}
            >
              <FaEdit size={14} />
              Edit
            </button>
          )}
          <button
            onClick={handleLoadPdf}
            disabled={loadingPdf}
            style={actionBtnStyle}
          >
            <FaFilePdf size={14} />
            {loadingPdf ? 'Loading...' : 'View PDF'}
          </button>
          <button
            onClick={handleDownloadPdf}
            style={actionBtnStyle}
          >
            <FaDownload size={14} />
            Download PDF
          </button>
          <button
            onClick={() => setShowEmailModal(true)}
            style={actionBtnStyle}
          >
            <FaEnvelope size={14} />
            Send via email
          </button>
          {invoice.status === 'DRAFT' && (
            <button
              onClick={() => handleStatusChange('SENT')}
              disabled={actionLoading}
              style={{ ...actionBtnStyle, background: '#021a4a', color: 'white', border: 'none' }}
            >
              <FaEnvelope size={14} />
              Mark as sent
            </button>
          )}
          {(invoice.status === 'DRAFT' || invoice.status === 'SENT') && (
            <button
              onClick={() => handleStatusChange('PAID')}
              disabled={actionLoading}
              style={{ ...actionBtnStyle, background: '#059669', color: 'white', border: 'none' }}
            >
              <FaCheck size={14} />
              Mark as paid
            </button>
          )}
          {invoice.status !== 'CANCELLED' && invoice.status !== 'PAID' && (
            <button
              onClick={() => handleStatusChange('CANCELLED')}
              disabled={actionLoading}
              style={{ ...actionBtnStyle, background: '#dc2626', color: 'white', border: 'none' }}
            >
              <FaTimes size={14} />
              Cancel
            </button>
          )}
        </div>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          padding: '24px',
          maxWidth: '800px',
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
          Line items
        </h3>

        {invoice.lines && invoice.lines.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fc', borderBottom: '2px solid #e0e3ef' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Item</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Unit price</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Discount</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lines.map((line, index) => (
                  <tr key={line.id || index} style={{ borderBottom: '1px solid #e0e3ef' }}>
                    <td style={tdStyle}>{line.position}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: '#021a4a' }}>
                        {line.description.split('\n')[0]}
                      </div>
                      {line.description.includes('\n') && (
                        <div style={{ fontSize: '12px', color: '#666', whiteSpace: 'pre-wrap' }}>
                          {line.description.split('\n').slice(1).join('\n')}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {line.quantity} {line.unit}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {InvoiceService.formatCurrency(line.unit_price_net)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {parseFloat(line.discount_percent) > 0 ? `${line.discount_percent}%` : '-'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                      {InvoiceService.formatCurrency(line.line_total_net || '0')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '2px solid #e0e3ef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>Subtotal net:</span>
                <span style={{ fontWeight: 600 }}>{InvoiceService.formatCurrency(invoice.net_total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666' }}>VAT:</span>
                <span style={{ fontWeight: 600 }}>{InvoiceService.formatCurrency(invoice.tax_total)}</span>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '12px',
                  borderTop: '2px solid #021a4a',
                  fontSize: '16px',
                }}
              >
                <span style={{ fontWeight: 700, color: '#021a4a' }}>Total gross:</span>
                <span style={{ fontWeight: 700, color: '#021a4a' }}>
                  {InvoiceService.formatCurrency(invoice.gross_total)}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding: '20px', textAlign: 'center', color: '#888' }}>
            No line items
          </div>
        )}

        {invoice.notes_internal && (
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#666' }}>
              Internal notes
            </h4>
            <div
              style={{
                padding: '12px',
                background: '#fffbeb',
                borderRadius: '6px',
                borderLeft: '4px solid #fbbf24',
              }}
            >
              <p style={{ margin: 0, fontSize: '14px', color: '#333', whiteSpace: 'pre-wrap' }}>
                {invoice.notes_internal}
              </p>
            </div>
          </div>
        )}
      </div>

      <PdfDrawer
        isOpen={showPdfDrawer}
        onClose={() => setShowPdfDrawer(false)}
        pdfBlobUrl={pdfBlobUrl}
        onRefresh={handleLoadPdf}
        loading={loadingPdf}
        invoiceNumber={invoice.invoice_number}
      />

      <SendEmailModal
        isOpen={showEmailModal}
        invoice={invoice}
        partnerEmail={partner?.email || ''}
        onClose={() => setShowEmailModal(false)}
        onSent={handleEmailSent}
      />
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  background: 'white',
  color: '#333',
  fontSize: '13px',
  fontWeight: 600,
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: '#666',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  color: '#333',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};
